export interface AdvertiserDashboard {
  totalAdvertisements: number;
  publishedCount: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
}

export type AdvertisementStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'PUBLISHED'
  | 'ARCHIVED';

export interface Advertisement {
  id: string;
  ownerId: string;
  title: string;
  description: string;
  category: 'FLOWERS' | 'COSMETICS' | 'JEWELRY';
  price: number;
  currency: string | null;
  city: string | null;
  freshUntil: string | null;
  status: AdvertisementStatus;
  createdAt: string;
  updatedAt: string | null;
  publishedAt: string | null;
}

export interface AdvertisementsResponse {
  advertisements: Advertisement[];
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

export interface AdvertiserProfile {
  id: string;
  keycloakId: string;
  email: string | null;
  username: string | null;
  telegramUserId: number | null;
  telegramUsername: string | null;
  firstName: string | null;
  lastName: string | null;
  languageCode: string | null;
}

export interface CreateAdvertisementRequest {
  title: string;
  description: string;
  category: Advertisement['category'];
  price: number;
  currency?: string | null;
  city?: string | null;
  freshUntil?: string | null;
  photoUrls: string[];
}

const API_BASE = '/api';

async function http<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init && init.headers),
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }

  return (await res.json()) as T;
}

export async function loginWithTelegram(initData: string): Promise<void> {
  await http(`${API_BASE}/auth/telegram`, {
    method: 'POST',
    body: JSON.stringify({ initData }),
  });
}

export async function refreshTelegramSession(): Promise<void> {
  await http(`${API_BASE}/auth/telegram/refresh`, {
    method: 'POST',
  });
}

export async function fetchAdvertiserDashboard(): Promise<AdvertiserDashboard> {
  return http(`${API_BASE}/telegram/advertiser/dashboard`);
}

export async function fetchAdvertiserAdvertisements(
  params?: { status?: AdvertisementStatus; page?: number; size?: number },
): Promise<AdvertisementsResponse> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.page) search.set('page', String(params.page));
  if (params?.size) search.set('size', String(params.size));

  const qs = search.toString();
  const url = `${API_BASE}/telegram/advertiser/advertisements${
    qs ? `?${qs}` : ''
  }`;
  return http(url);
}

export async function fetchAdvertiserProfile(): Promise<AdvertiserProfile> {
  return http(`${API_BASE}/telegram/advertiser/profile`);
}

export async function updateAdvertiserLanguage(
  languageCode: string,
): Promise<{ message: string; languageCode: string }> {
  return http(`${API_BASE}/telegram/advertiser/settings/language`, {
    method: 'PUT',
    body: JSON.stringify({ languageCode }),
  });
}

export async function createAdvertisement(
  payload: CreateAdvertisementRequest,
): Promise<{ id: string; status: string; message: string }> {
  return http(`${API_BASE}/telegram/advertisements`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
